import { useEffect, useMemo, useState } from 'react';

import { Tick01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useGetLabelColor } from 'hooks/color-contrast-checker';

import { IconComponent } from 'components/Icon/IconComponent';

import { Input } from './../Input/Input';
import styles from './MultipleChoice.module.scss';

const removeValue = (array, value) => {
  const index = array.indexOf(value);
  if (index < 0) return array;
  array.splice(index, 1);
  return array;
};

export const MultipleChoice = ({
  value,
  defaultValue,
  className,
  options: { multipleSelection, options, otherOption },
  onChange,
  disabled,
  brand = {},
  yesNo = false,
  radioButtonStyle = false,
  prefixIcon = false,
  error,
  noErrorMessage,
}) => {
  const actualValue = value !== undefined ? value : defaultValue;

  // States to handle other option
  const [otherOptionEnabled, setOtherOptionEnabled] = useState(false);
  const [otherOptionValue, setOtherOptionValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const brandColor = brand?.color_button || '#4E18BB';
  const brandTextColor = brand?.color_button_text || '#FFFFFF';

  const getLabelColor = useGetLabelColor(brandColor, `${brandColor}30`, brandTextColor);

  const getSelectedStyles = useMemo(
    () => (selected, brandColor) => ({
      container: {
        backgroundColor: selected ? `${brandColor}20` : '',
        borderColor: selected ? brandColor : '',
      },
      label: {
        backgroundColor: selected ? `${brandColor}30` : '',
        color: selected ? getLabelColor : '',
      },
      checkbox: {
        backgroundColor: selected ? brandColor : '',
        borderColor: selected ? brandTextColor : '',
      },
    }),
    [getLabelColor, brandTextColor],
  );

  // On option clicked (except for "Other")
  const handleOptionSelected = selectedValue => {
    if (disabled) return;
    if (multipleSelection) {
      if (!actualValue) {
        onChange([selectedValue]);
      } else if (actualValue.includes(selectedValue)) {
        onChange(removeValue(actualValue, selectedValue));
      } else {
        onChange([...actualValue, selectedValue]);
      }
    } else {
      setOtherOptionEnabled(false);
      onChange(selectedValue);
    }
  };

  useEffect(() => {
    if (!yesNo || !isFocused) return;
    const handleKeyDown = async event => {
      switch (event.key) {
        case 'y':
          event.preventDefault();
          handleOptionSelected('Yes');
          break;
        case 'n':
          event.preventDefault();
          handleOptionSelected('No');
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [yesNo, isFocused]);

  // Check if option selected
  const isSelected = optionValue => {
    if (!actualValue) return false;

    if (multipleSelection) {
      return actualValue.includes(optionValue);
    } else {
      return (
        (actualValue ?? '').toString().toLowerCase() ===
        (optionValue ?? '').toString().toLowerCase()
      );
    }
  };

  // On "Other" option clicked
  const handleOtherOptionClick = () => {
    if (multipleSelection) {
      if (otherOptionEnabled) {
        onChange(removeValue(actualValue, otherOptionValue));
        setOtherOptionEnabled(false);
      } else {
        setOtherOptionEnabled(true);
      }
    } else {
      onChange(otherOptionValue);
      setOtherOptionEnabled(true);
    }
  };

  const handleOtherOptionInputChange = e => {
    const newValue = e.target.value;

    if (multipleSelection) {
      if (!actualValue) {
        onChange([newValue]);
      } else {
        const otherOptionIndex = actualValue.indexOf(otherOptionValue);
        if (otherOptionIndex < 0) {
          onChange([...actualValue, newValue]);
        } else {
          onChange([
            ...actualValue.slice(0, otherOptionIndex),
            newValue,
            ...actualValue.slice(otherOptionIndex + 1),
          ]);
        }
      }
    } else {
      onChange(newValue);
    }

    setOtherOptionValue(newValue);
  };

  return (
    <div
      className={cn(styles.root, {
        [styles.rootRadioButton]: radioButtonStyle,
        [className]: className,
      })}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      tabIndex={disabled ? -1 : 0}>
      {options?.map((option, index) => (
        <Option
          name={option.name}
          key={option.value}
          icon={option?.icon || null}
          description={option?.description || null}
          index={index}
          value={option.value}
          selected={isSelected(option.value)}
          multipleSelection={multipleSelection}
          onClick={handleOptionSelected}
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          brandColor={brandColor}
          brandTextColor={brandTextColor}
          getSelectedStyles={getSelectedStyles}
          yesNo={yesNo}
          prefixIcon={prefixIcon}
        />
      ))}
      {otherOption && (
        <div
          className={cn(styles.option, styles.otherOption, {
            [styles.optionSelected]: otherOptionEnabled,
          })}
          style={getSelectedStyles(otherOptionEnabled, brandColor).container}>
          <div
            tabIndex={disabled ? -1 : 0}
            onClick={handleOtherOptionClick}
            onKeyDown={e => {
              if (e.key === ' ') {
                e.preventDefault();
                handleOtherOptionClick();
              }
            }}
            role="button"
            aria-pressed={otherOptionEnabled}
            className={cn(styles.title, 'd-flex align-items-center justify-content-between')}>
            <div className="d-flex align-items-stretch justify-content-start">
              <div
                className={styles.optionLabel}
                style={getSelectedStyles(otherOptionEnabled, brandColor).label}>
                {String.fromCharCode(65 + options.length)}
              </div>
              <span className={styles.optionText}>Other</span>
            </div>
            {multipleSelection ? (
              <div
                className={styles.checkbox}
                style={getSelectedStyles(otherOptionEnabled, brandColor).checkbox}>
                <FeatherIcon
                  className={styles.checkIcon}
                  icon="check"
                  size={13}
                  fill={otherOptionEnabled ? brandColor : ''}
                  stroke={otherOptionEnabled ? brandTextColor : ''}
                />
              </div>
            ) : (
              <div className={styles.radioButton}></div>
            )}
          </div>
          {otherOptionEnabled && (
            <>
              <div style={{ height: '1px', backgroundColor: brandColor, width: '100%' }} />
              <Input
                className={styles.input}
                inputProps={{
                  value: otherOptionValue,
                  onChange: handleOtherOptionInputChange,
                }}
              />
            </>
          )}
        </div>
      )}
      {error && error?.message && !noErrorMessage && (
        <span className={styles.error}>{error.message}</span>
      )}
    </div>
  );
};

const Option = ({
  name,
  value,
  icon = null,
  description = null,
  selected = false,
  multipleSelection = false,
  onClick,
  disabled,
  tabIndex,
  index,
  brandColor,
  brandTextColor,
  getSelectedStyles,
  yesNo = false,
  prefixIcon = false,
}) => (
  <div
    className={cn(styles.option, 'd-flex align-items-center justify-content-between', {
      [styles.optionSelected]: selected,
      [styles.disabled]: disabled,
      'u-padding': prefixIcon,
    })}
    style={getSelectedStyles(selected, brandColor).container}
    onClick={() => onClick(value)}
    onKeyDown={e => {
      if (e.key === ' ') {
        e.preventDefault();
        onClick(value);
      }
    }}
    tabIndex={tabIndex}
    role="button"
    aria-pressed={selected}>
    <div
      className={cn('d-flex align-items-stretch justify-content-start u-width-90', {
        'u-padding--small': prefixIcon,
      })}>
      {prefixIcon ? (
        <IconComponent icon={icon} />
      ) : (
        <div className={styles.optionLabel} style={getSelectedStyles(selected, brandColor).label}>
          {yesNo ? (value === 'Yes' ? 'Y' : 'N') : String.fromCharCode(65 + index) || index}
        </div>
      )}
      <div
        className={cn('d-flex flex-column', {
          'u-padding--small': description,
        })}>
        <span
          className={
            description ? 'u-font-size-small u-font-weight-400 primary-text' : styles.optionText
          }>
          {name || value}
        </span>
        {description && <span className="secondary-text u-font-size-x-small">{description}</span>}
      </div>
    </div>
    {multipleSelection ? (
      <div className={styles.checkbox} style={getSelectedStyles(selected, brandColor).checkbox}>
        <FeatherIcon
          className={styles.checkIcon}
          icon="check"
          size={20}
          fill={brandColor}
          stroke={brandTextColor}
        />
      </div>
    ) : (
      <div
        className={styles.radioButton}
        style={getSelectedStyles(selected, brandColor).checkbox}></div>
    )}
  </div>
);
